import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ratingAPI, projectAPI, proposalAPI, notificationAPI, chatAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { CsfQuickUploadButton } from '../components/CsfQuickUploadButton';
import {
  Briefcase,
  FileText,
  ChartLine,
  Star,
  ArrowRight,
  Clock,
  CheckCircle,
  Plus,
  Bell,
  ChartLineUp,
  Package,
  ChatCircleDots,
  ShieldWarning,
} from '@phosphor-icons/react';

const DashboardPage = () => {
  const { user, activeRole, isClient, isProvider, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentProjects, setRecentProjects] = useState([]);
  const [availableQuotations, setAvailableQuotations] = useState([]);
  const [myProposals, setMyProposals] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [chatRooms, setChatRooms] = useState([]);
  const [csfBlock, setCsfBlock] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [activeRole]);

  const fetchDashboardData = async () => {
    setLoading(true);
    const tasks = [
      ratingAPI.getDashboardStats(activeRole).then((r) => setStats(r.data)),
      notificationAPI.getAll(true).then((r) => setNotifications(r.data.slice(0, 5))),
      chatAPI.getMyRooms()
        .then((r) => {
          const sorted = (r.data || []).sort((a, b) => {
            const ta = new Date(a.last_message_time || a.created_at || 0).getTime();
            const tb = new Date(b.last_message_time || b.created_at || 0).getTime();
            return tb - ta;
          });
          setChatRooms(sorted.slice(0, 5));
        })
        .catch(() => setChatRooms([])),
    ];
    if (isClient || activeRole === 'cliente') {
      tasks.push(projectAPI.getAll().then((r) => setRecentProjects(r.data.slice(0, 5))));
    }
    if (isProvider || activeRole === 'proveedor') {
      tasks.push(
        proposalAPI.getAvailable()
          .then((r) => setAvailableQuotations((r.data || []).slice(0, 5)))
          .catch((err) => {
            // Provider gated: surface CSF block on the dashboard so they fix it fast
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 403 && detail && typeof detail === 'object' && detail.code === 'csf_required') {
              setCsfBlock(detail);
            }
            setAvailableQuotations([]);
          }),
        proposalAPI.getMine()
          .then((r) => setMyProposals((r.data || []).slice(0, 5)))
          .catch(() => setMyProposals([])),
      );
    }
    const results = await Promise.allSettled(tasks);
    results.filter((r) => r.status === 'rejected')
      .forEach((r) => console.error('Failed to fetch dashboard data:', r.reason));
    setLoading(false);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buenos días';
    if (hour < 18) return 'Buenas tardes';
    return 'Buenas noches';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { label: 'Borrador', variant: 'secondary' },
      sent: { label: 'Enviado', variant: 'default' },
      pending: { label: 'Pendiente', variant: 'secondary' },
      selected: { label: 'Seleccionada', variant: 'success' },
      accepted: { label: 'Aceptada', variant: 'success' },
      rejected: { label: 'Rechazada', variant: 'destructive' },
      expired: { label: 'Vencida', variant: 'secondary' },
      in_progress: { label: 'En Progreso', variant: 'warning' },
      completed: { label: 'Completado', variant: 'success' },
      cancelled: { label: 'Cancelado', variant: 'destructive' },
    };
    const config = statusMap[status] || { label: status, variant: 'secondary' };
    return <Badge variant={config.variant} className="rounded-none text-xs">{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-32 bg-zinc-200 rounded-none"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <div key={i} className="h-32 bg-zinc-200 rounded-none"></div>)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome Section */}
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-100">
              {getGreeting()}, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-zinc-600 dark:text-zinc-400 mt-1">
              {isClient || activeRole === 'cliente' 
                ? 'Gestiona tus proyectos y cotizaciones' 
                : 'Encuentra nuevas oportunidades de negocio'}
            </p>
          </div>
          
          {(isClient || activeRole === 'cliente') && (
            <Link to="/projects/new" data-testid="new-project-btn">
              <Button className="btn-primary rounded-none">
                <Plus className="w-5 h-5 mr-2" />
                Nuevo Proyecto
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* CSF blocking banner — providers without valid Constancia de Situación Fiscal */}
      {csfBlock && (
        <div
          className="border-2 border-orange-500 bg-orange-50 dark:bg-orange-950/30 p-5 sm:p-6 flex flex-col sm:flex-row gap-4"
          data-testid="dashboard-csf-banner"
        >
          <div className="w-12 h-12 bg-orange-500 text-white flex items-center justify-center flex-shrink-0">
            <ShieldWarning className="w-7 h-7" weight="fill" />
          </div>
          <div className="flex-1 space-y-2">
            <h3 className="font-bold text-orange-900 dark:text-orange-200 text-base uppercase tracking-wide">
              {csfBlock.reason === 'csf_expired'
                ? 'Tu Constancia de Situación Fiscal venció'
                : csfBlock.reason === 'csf_invalid'
                  ? 'Tu Constancia de Situación Fiscal no coincide con tus datos'
                  : csfBlock.reason === 'profile_incomplete'
                    ? 'Completa tu perfil de proveedor'
                    : 'Constancia de Situación Fiscal requerida'}
            </h3>
            <p className="text-sm text-orange-800 dark:text-orange-200 leading-relaxed">
              {csfBlock.message}
            </p>
            {Array.isArray(csfBlock.errors) && csfBlock.errors.length > 0 && (
              <ul className="text-xs text-orange-700 dark:text-orange-300 list-disc list-inside space-y-0.5 pt-1">
                {csfBlock.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
            <div className="pt-3 flex flex-col sm:flex-row gap-2">
              {csfBlock.reason !== 'profile_incomplete' && (
                <CsfQuickUploadButton
                  testId="dashboard-csf-quick-upload-btn"
                  onSuccess={() => { setCsfBlock(null); fetchDashboardData(); }}
                />
              )}
              <Link to="/profile">
                <Button
                  className="rounded-none bg-orange-600 hover:bg-orange-700 text-white h-10 px-5"
                  data-testid="dashboard-csf-fix-btn"
                >
                  <FileText className="w-4 h-4 mr-2" weight="bold" />
                  Actualizar mi información fiscal
                </Button>
              </Link>
              <a
                href="https://www.sat.gob.mx/aplicacion/login/53027/emite-tu-constancia-de-situacion-fiscal"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center text-sm text-orange-700 dark:text-orange-300 underline h-10 px-2"
                data-testid="dashboard-csf-sat-link"
              >
                Obtener constancia en el SAT →
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="border-zinc-200 rounded-none">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">
                  {isClient || activeRole === 'cliente' ? 'Proyectos' : 'Propuestas'}
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-zinc-100 mt-1">{stats?.main_count || 0}</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 flex items-center justify-center">
                {isClient || activeRole === 'cliente' 
                  ? <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                  : <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                }
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-none">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">Completados</p>
                <p className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-zinc-100 mt-1">{stats?.completed_count || 0}</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-none">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">Calificación</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-zinc-100">{stats?.rating_avg?.toFixed(1) || '5.0'}</p>
                  <Star className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" weight="fill" />
                </div>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-100 flex items-center justify-center">
                <Star className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" weight="fill" />
              </div>
            </div>
            <p className="text-xs text-zinc-500 mt-2">{stats?.rating_count || 0} calificaciones</p>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 rounded-none">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">Notificaciones</p>
                <p className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-zinc-100 mt-1">{notifications.length}</p>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 flex items-center justify-center">
                <Bell className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client: Recent Projects */}
          {(isClient || activeRole === 'cliente') && (
            <Card className="border-zinc-200 rounded-none">
              <CardHeader className="border-b border-zinc-200">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-bold">Proyectos Recientes</CardTitle>
                  <Link to="/projects" className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1">
                    Ver todos <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {recentProjects.length === 0 ? (
                  <div className="p-8 text-center">
                    <Package className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                    <p className="text-zinc-500 mb-4">No tienes proyectos aún</p>
                    <Link to="/projects/new">
                      <Button className="btn-primary rounded-none">
                        <Plus className="w-4 h-4 mr-2" />
                        Crear Proyecto
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-200">
                    {recentProjects.map((project) => (
                      <Link 
                        key={project.project_id} 
                        to={`/projects/${project.project_id}`}
                        className="block p-4 hover:bg-zinc-50 transition-colors"
                        data-testid={`project-${project.project_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-zinc-900">{project.name}</h3>
                            <p className="text-sm text-zinc-500 mt-1">
                              {project.total_materials} materiales • {project.total_categories} categorías
                            </p>
                          </div>
                          {getStatusBadge(project.status)}
                        </div>
                        {project.sub_quotations_count > 0 && (
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
                              <span>Progreso de cotizaciones</span>
                              <span>{project.sub_quotations_with_winner}/{project.sub_quotations_count}</span>
                            </div>
                            <Progress 
                              value={(project.sub_quotations_with_winner / project.sub_quotations_count) * 100} 
                              className="h-2 rounded-none"
                            />
                          </div>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Provider: Available Quotations */}
          {(isProvider || activeRole === 'proveedor') && (
            <Card className="border-zinc-200 rounded-none">
              <CardHeader className="border-b border-zinc-200">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-bold">Cotizaciones Disponibles</CardTitle>
                  <Link to="/quotations" className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1">
                    Ver todas <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {availableQuotations.length === 0 ? (
                  <div className="p-8 text-center">
                    <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                    <p className="text-zinc-500">No hay cotizaciones disponibles</p>
                    <p className="text-sm text-zinc-400 mt-2">
                      {csfBlock
                        ? 'Actualiza tu información fiscal para ver cotizaciones'
                        : 'Asegúrate de tener tus especialidades configuradas'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-200">
                    {availableQuotations.map((quotation) => (
                      <Link 
                        key={quotation.quotation_id} 
                        to={`/quotations/${quotation.quotation_id}`}
                        className="block p-4 hover:bg-zinc-50 transition-colors"
                        data-testid={`quotation-${quotation.quotation_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-zinc-900">{quotation.name}</h3>
                            <div className="flex items-center gap-3 mt-1">
                              <Badge variant="outline" className="rounded-none text-xs">
                                {quotation.category}
                              </Badge>
                              <span className="text-sm text-zinc-500">
                                {quotation.materials?.length || 0} materiales
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-1 text-amber-600">
                              <Star className="w-4 h-4" weight="fill" />
                              <span className="text-sm font-medium">
                                {quotation.client_rating?.toFixed(1) || '5.0'}
                              </span>
                            </div>
                            <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(quotation.deadline).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Provider: My Proposals */}
          {(isProvider || activeRole === 'proveedor') && myProposals.length > 0 && (
            <Card className="border-zinc-200 rounded-none">
              <CardHeader className="border-b border-zinc-200">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-bold">Mis Propuestas</CardTitle>
                  <Link to="/proposals" className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1">
                    Ver todas <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-zinc-200">
                  {myProposals.map((proposal) => (
                    <Link 
                      key={proposal.proposal_id} 
                      to={`/proposals/${proposal.proposal_id}`}
                      className="block p-4 hover:bg-zinc-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-zinc-900">{proposal.quotation_name}</h3>
                          <p className="text-sm text-zinc-500 mt-1">
                            Total: ${proposal.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {proposal.is_winner && (
                            <Badge className="bg-green-500 rounded-none">Ganador</Badge>
                          )}
                          {getStatusBadge(proposal.status)}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Notifications */}
          <Card className="border-zinc-200 rounded-none">
            <CardHeader className="border-b border-zinc-200">
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Notificaciones
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {notifications.length === 0 ? (
                <div className="p-6 text-center text-zinc-500">
                  No hay notificaciones nuevas
                </div>
              ) : (
                <div className="divide-y divide-zinc-200">
                  {notifications.map((notif) => (
                    <div 
                      key={notif.notification_id} 
                      className={`p-4 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors ${notif.link ? 'cursor-pointer' : ''}`}
                      onClick={() => {
                        if (notif.link) {
                          if (!notif.read) {
                            notificationAPI.markRead(notif.notification_id).catch(() => {});
                          }
                          navigate(notif.link);
                        }
                      }}
                      data-testid={`dashboard-notif-${notif.notification_id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{notif.title}</p>
                          <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{notif.message}</p>
                        </div>
                        {notif.link && (
                          <ArrowRight className="w-4 h-4 text-orange-400 flex-shrink-0 mt-1" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {notifications.length > 0 && (
                <div className="p-3 border-t border-zinc-200 dark:border-zinc-700">
                  <Link to="/notifications" className="text-xs text-orange-600 hover:text-orange-700 font-medium flex items-center justify-center gap-1">
                    Ver todas las notificaciones <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="border-zinc-200 rounded-none">
            <CardHeader className="border-b border-zinc-200">
              <CardTitle className="text-lg font-bold">Acciones Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {(isClient || activeRole === 'cliente') && (
                <>
                  <Link to="/projects/new" className="block">
                    <Button variant="outline" className="w-full rounded-none justify-start">
                      <Plus className="w-4 h-4 mr-2" />
                      Nuevo Proyecto
                    </Button>
                  </Link>
                  <Link to="/price-consultation" className="block">
                    <Button variant="outline" className="w-full rounded-none justify-start">
                      <ChartLineUp className="w-4 h-4 mr-2" />
                      Consultar Precios
                    </Button>
                  </Link>
                </>
              )}
              {(isProvider || activeRole === 'proveedor') && (
                <>
                  <Link to="/quotations" className="block">
                    <Button variant="outline" className="w-full rounded-none justify-start">
                      <FileText className="w-4 h-4 mr-2" />
                      Ver Cotizaciones
                    </Button>
                  </Link>
                  <Link to="/profile" className="block">
                    <Button variant="outline" className="w-full rounded-none justify-start">
                      <ChartLine className="w-4 h-4 mr-2" />
                      Editar Especialidades
                    </Button>
                  </Link>
                </>
              )}
              <Link to="/chat" className="block">
                <Button variant="outline" className="w-full rounded-none justify-start">
                  <Package className="w-4 h-4 mr-2" />
                  Mis Conversaciones
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent Messages — clickable, each opens the chat for that quotation */}
      <Card className="border-zinc-200 rounded-none" data-testid="dashboard-recent-messages">
        <CardHeader className="border-b border-zinc-200 flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ChatCircleDots className="w-5 h-5 text-orange-500" />
            Mensajes Recientes
          </CardTitle>
          <Link to="/chat">
            <Button variant="ghost" size="sm" className="rounded-none text-orange-600 hover:bg-orange-50" data-testid="view-all-messages">
              Ver todos <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent className="p-0">
          {chatRooms.length === 0 ? (
            <div className="p-8 text-center">
              <ChatCircleDots className="w-12 h-12 mx-auto text-zinc-300 mb-2" />
              <p className="text-zinc-500 dark:text-zinc-400 text-sm">Aún no tienes conversaciones activas.</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {chatRooms.map((room) => {
                const unread = room.unread_count || 0;
                const isActive = room.is_active !== false;
                const when = room.last_message_time || room.created_at;
                const timeLabel = when
                  ? new Date(when).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                  : '';
                return (
                  <Link
                    key={room.quotation_id}
                    to={`/quotations/${room.quotation_id}`}
                    className="flex items-start gap-3 p-4 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
                    data-testid={`dashboard-message-${room.quotation_id}`}
                  >
                    <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${unread > 0 ? 'bg-orange-500 text-white' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500'}`}>
                      <ChatCircleDots className="w-5 h-5" weight={unread > 0 ? 'fill' : 'bold'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-sm truncate ${unread > 0 ? 'font-bold text-zinc-900 dark:text-zinc-100' : 'font-semibold text-zinc-700 dark:text-zinc-300'}`}>
                          {room.other_user_name || 'Usuario'}
                        </p>
                        <span className="text-xs text-zinc-400 flex-shrink-0">{timeLabel}</span>
                      </div>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">
                        {room.quotation_name} {room.quotation_key ? `· ${room.quotation_key}` : ''}
                      </p>
                      {room.last_message && (
                        <p className={`text-sm truncate mt-1 ${unread > 0 ? 'text-zinc-800 dark:text-zinc-200' : 'text-zinc-500 dark:text-zinc-400'}`}>
                          {room.last_message}
                        </p>
                      )}
                      {!isActive && (
                        <span className="inline-block text-[10px] uppercase tracking-wider text-zinc-400 mt-1">
                          Chat cerrado
                        </span>
                      )}
                    </div>
                    {unread > 0 && (
                      <Badge className="bg-orange-500 text-white rounded-none flex-shrink-0">{unread}</Badge>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
